import { formatRateWithQuotingUnit } from "utils";

import type {
  CarrierBookingSharedMetadata,
  CarrierDirectBookingAssignedMetadata,
  CarrierMultiShipmentLoadsAssignedMetadata,
  CarrierProjectLostMetadata,
  CarrierShipmentMatchedMetadata,
  FollowedCarrierNewShipmentMetadata,
  FollowShipperRequestAcceptedMetadata,
  FollowShipperRequestInternalMetadata,
  JoinCompanyRequestCreatedMetadata,
  JoinCompanyResponseMetadata,
  PrivateShipmentCreatedNotificationMetadata,
  ShipmentBookedChatChannelMetadata,
  ShipperNewQuoteMetadata,
  ShipperProjectCompletedMetadata,
  ShipperShipmentTemplateSharedMetadata,
  ShipperTfcAmendedMetadata,
  SupplierCustomerNetworkInviteMetadata,
} from "./types";

// Remember to add new NotificationTypes to frontend/src/global.d.ts/NotificationType
// and to FRONTEND_RENDERABLE_NOTIFICATION_TYPES in
// application/notification/models.py, which the backend uses to hide types
// listed in neither this object nor CUSTOM_FORMATTED_NOTIFICATION_TYPES.
const SUPPORTED_NOTIFICATIONS = {
  SHIPPER_COUNTER_QUOTE_RECEIVED: (shipmentRef: number | string) =>
    `There is a counter-quote on shipment ${shipmentRef}. Click to view details.`,
  SHIPPER_COUNTER_QUOTE_EXPIRED: (shipmentRef: number | string) =>
    `Your counter-quote on shipment ${shipmentRef} has expired. Click to view other quotes.`,
  SHIPPER_QUESTION_ASKED: (shipmentRef: number | string) =>
    `You've received a question about shipment ${shipmentRef}. Click to view and answer now.`,

  CARRIER_COUNTER_QUOTE_RECEIVED: (shipmentRef: number | string) =>
    `The shipper countered your quote on shipment ${shipmentRef}. Click to accept or counter their quote.`,
  CARRIER_QUOTE_EXPIRED: (shipmentRef: number | string) =>
    `Your quote on shipment ${shipmentRef} has expired. Click to view details.`,
  CARRIER_SHIPMENT_CANCELLED: (shipmentRef: number | string) =>
    `Shipment ${shipmentRef} has been cancelled by the shipper. Click to view details.`,
  CARRIER_QUESTION_ANSWERED: (shipmentRef: number | string) =>
    `Your question about shipment ${shipmentRef} has been answered! Click to view the answer now.`,
  CARRIER_QUOTE_QUESTION_ASKED: (shipmentRef: number | string) =>
    `The shipper has asked a question about your quote on shipment ${shipmentRef}. Click here to answer.`,
  SHIPPER_QUOTE_QUESTION_ANSWERED: (shipmentRef: number | string) =>
    `The carrier has answered your question on shipment ${shipmentRef}. Click here to view.`,
  SHIPPER_SHIPMENT_TRACKING_LINK_ADDED: (shipmentRef: number | string) =>
    `The carrier has added a tracking link to shipment ${shipmentRef}. Click here to view.`,
  SHIPPER_SHIPMENT_TRACKING_LINK_DELETED: (shipmentRef: number | string) =>
    `The carrier has deleted the tracking link from shipment ${shipmentRef}. Click here to view the shipment.`,
  CARRIER_SHIPMENT_CANCELLATION_FEEDBACK_CREATED: (
    shipmentRef: number | string,
  ) =>
    `The shipper has left cancellation feedback on shipment ${shipmentRef}. Click here to view.`,
  SHIPPER_SHIPMENT_SHARED: (shipmentRef: number | string) =>
    `Shipment ${shipmentRef} has been shared with you. Click here to view the shipment.`,
  CARRIER_QUOTE_DECLINED: (shipmentRef: number | string) =>
    `The shipper has declined your quote on shipment ${shipmentRef}. Click here to view the feedback.`,
  SHIPPER_QUOTE_DECLINED: (shipmentRef: number | string) =>
    `The carrier has declined your counter quote on shipment ${shipmentRef}. Click here to view the feedback.`,
  SHIPPER_SHIPMENT_BOOKING_CANCELLED: (shipmentRef: number | string) =>
    `The booking for shipment ${shipmentRef} has been cancelled. Click here to view details.`,
  CARRIER_SHIPMENT_BOOKING_CANCELLED: (shipmentRef: number | string) =>
    `The booking for shipment ${shipmentRef} has been cancelled. Click here to view details.`,
  SHIPPER_SHIPMENT_MARKED_DELIVERED: (shipmentRef: number | string) =>
    `Shipment ${shipmentRef} has been marked as delivered. Click here to view details.`,
  CARRIER_SHIPMENT_MARKED_DELIVERED: (shipmentRef: number | string) =>
    `Shipment ${shipmentRef} has been marked as delivered. Click here to view details.`,
  SHIPPER_SHIPMENT_MARKED_PICKED_UP: (shipmentRef: number | string) =>
    `Shipment ${shipmentRef} has been marked as picked up. Click here to view details.`,
  CARRIER_SHIPMENT_MARKED_PICKED_UP: (shipmentRef: number | string) =>
    `Shipment ${shipmentRef} has been marked as picked up. Click here to view details.`,
  SHIPPER__ROUTE_POINT_ETA_CREATED: (shipmentRef: number | string) =>
    `An ETA has been added to shipment ${shipmentRef}. Click here to view details.`,

  // Total Freight Cost settlement notifications (SHIPPER__TFC_AMENDED is custom-formatted below)
  SHIPPER__TFC_SUBMITTED: (shipmentRef: number | string) =>
    `The total freight cost for shipment ${shipmentRef} is now ready for review. Click to review.`,
  SHIPPER__TFC_RESUBMITTED: (shipmentRef: number | string) =>
    `The total freight cost for shipment ${shipmentRef} has been resubmitted. Click to review.`,
  CARRIER__TFC_APPROVED: (shipmentRef: number | string) =>
    `The shipper has approved the total freight cost on shipment ${shipmentRef}. Click to view shipment.`,
  CARRIER__TFC_REJECTED: (shipmentRef: number | string) =>
    `The shipper has rejected the total freight cost on shipment ${shipmentRef}. Click to review.`,
  CARRIER__TFC_UNDO_AND_ACCEPTED: (shipmentRef: number | string) =>
    `The shipper has approved the total freight cost on shipment ${shipmentRef}. Click to view shipment.`,

  // Supplier shipment customer notifications
  SUPPLIER_SHIPMENT_CUSTOMER__ROUTE_POINT_ETA_CREATED: (
    shipmentRef: number | string,
  ) =>
    `An ETA has been added to supplier shipment ${shipmentRef}. Click here to view details.`,
  SUPPLIER_SHIPMENT_CUSTOMER__SHIPMENT_PICKED_UP: (
    shipmentRef: number | string,
  ) =>
    `Supplier shipment ${shipmentRef} has been marked as picked up. Click here to view details.`,
  SUPPLIER_SHIPMENT_CUSTOMER__SHIPMENT_DELIVERED: (
    shipmentRef: number | string,
  ) =>
    `Supplier shipment ${shipmentRef} has been delivered. Click here to view details.`,
  SUPPLIER_SHIPMENT_CUSTOMER__BOOKING_CANCELLED: (
    shipmentRef: number | string,
  ) =>
    `The booking for supplier shipment ${shipmentRef} has been cancelled. Click here to view details.`,
  SUPPLIER_SHIPMENT_CUSTOMER__TRACKING_LINK_ADDED: (
    shipmentRef: number | string,
  ) =>
    `A tracking link has been added to supplier shipment ${shipmentRef}. Click here to view details.`,
};

// Keep in sync with FRONTEND_RENDERABLE_NOTIFICATION_TYPES in
// application/notification/models.py, same as SUPPORTED_NOTIFICATIONS.
const CUSTOM_FORMATTED_NOTIFICATION_TYPES = {
  SHIPPER_NEW_QUOTE: "SHIPPER_NEW_QUOTE",
  CARRIER_FOLLOW_SHIPPER_REQUESTED_INTERNAL:
    "CARRIER_FOLLOW_SHIPPER_REQUESTED_INTERNAL",
  CARRIER_FOLOWED_SHIPPER_SHIPMENT_CREATED:
    "CARRIER_FOLOWED_SHIPPER_SHIPMENT_CREATED",
  CARRIER_FOLLOW_SHIPPER_RESPONSE_ACCEPTED:
    "CARRIER_FOLLOW_SHIPPER_RESPONSE_ACCEPTED",
  COMPANY_REVIEW_CREATED: "COMPANY_REVIEW_CREATED",
  SHIPMENT_BOOKED_CHAT_CHANNEL_CREATED: "SHIPMENT_BOOKED_CHAT_CHANNEL_CREATED",
  CARRIER_PRIVATE_SHIPMENT_INVITE: "CARRIER_PRIVATE_SHIPMENT_INVITE",
  CARRIER_PRIVATE_SHPMENT_ALL_NETWORK_INVITE:
    "CARRIER_PRIVATE_SHPMENT_ALL_NETWORK_INVITE",
  CARRIER_FAILED_TO_JOIN_NETWORK_ON_INSUFFICIENT_SLOTS:
    "CARRIER_FAILED_TO_JOIN_NETWORK_ON_INSUFFICIENT_SLOTS",
  CARRIER_ESSENTIALS_REACHED_MAXIMUM_BOOKINGS:
    "CARRIER_ESSENTIALS_REACHED_MAXIMUM_BOOKINGS",
  PAID_SUBSCRIPTION_CARRIER_REACHED_MAXIMUM_BOOKINGS:
    "PAID_SUBSCRIPTION_CARRIER_REACHED_MAXIMUM_BOOKINGS",
  CARRIER_SHIPMENT_MATCHED: "CARRIER_SHIPMENT_MATCHED",
  CARRIER_MULTI_SHIPMENT_PROJECT_LOADS_ASSIGNED:
    "CARRIER_MULTI_SHIPMENT_PROJECT_LOADS_ASSIGNED",
  CARRIER_SHIPMENT_LOST: "CARRIER_SHIPMENT_LOST",
  SHIPPER_PROJECT_COMPLETED: "SHIPPER_PROJECT_COMPLETED",
  CARRIER_BOOKING_SHARED: "CARRIER_BOOKING_SHARED",
  SUPPLIER_CUSTOMER_NETWORK_INVITE_CREATED:
    "SUPPLIER_CUSTOMER_NETWORK_INVITE_CREATED",
  CARRIER_CSF_SHIPMENT_INVITE_CREATED: "CARRIER_CSF_SHIPMENT_INVITE_CREATED",
  SHIPPER__TFC_AMENDED: "SHIPPER__TFC_AMENDED",
  JOIN_COMPANY_REQUEST_CREATED: "JOIN_COMPANY_REQUEST_CREATED",
  JOIN_COMPANY_REQUEST_APPROVED: "JOIN_COMPANY_REQUEST_APPROVED",
  JOIN_COMPANY_REQUEST_DENIED: "JOIN_COMPANY_REQUEST_DENIED",
  CARRIER_PRIVATE_SHIPMENT_CANCELLED: "CARRIER_PRIVATE_SHIPMENT_CANCELLED",
  CARRIER_DIRECT_BOOKING_ASSIGNED: "CARRIER_DIRECT_BOOKING_ASSIGNED",
  SHIPPER_SHIPMENT_TEMPLATE_SHARED: "SHIPPER_SHIPMENT_TEMPLATE_SHARED",
};

function getShipperShipmentTemplateSharedText(
  metadata: ShipperShipmentTemplateSharedMetadata,
): string {
  return `${metadata.shared_by_name} shared the "${metadata.template_name}" template with you! Click to view template.`;
}

function getFollowShipperRequestInternalText(
  meta: FollowShipperRequestInternalMetadata,
): string {
  return `${meta.first_name} ${meta.last_name} at ${meta.company_name} invites you to follow them to be notified about their shipments. Click to respond now.`;
}

function getFollowedCarrierNewShipmentText(
  metadata: FollowedCarrierNewShipmentMetadata,
): string {
  return `${metadata.first_name} ${metadata.last_name} at ${metadata.company_name} has posted a new ${metadata?.is_project ? "project" : "shipment"} ! Click to submit a quote now.`;
}

function getShipperFollowRequestAcceptedText(
  metadata: FollowShipperRequestAcceptedMetadata,
): string {
  const companyText = metadata.company_name
    ? ` at ${metadata.company_name}`
    : "";
  return `${metadata.first_name} ${metadata.last_name}${companyText} is now following you!`;
}

function getShipmentBookedChatChannelCreated(
  metadata: ShipmentBookedChatChannelMetadata,
): string {
  return `Congrats on booking shipment ${metadata.shipment_id}! Click to chat with ${metadata.counterpart_first_name} from ${metadata.counterpart_company_name}.`;
}

function getPrivateShipmentCreatedMetadata(
  metadata: PrivateShipmentCreatedNotificationMetadata,
): string {
  return `${metadata.company_name} just posted ${metadata?.is_project ? "project" : "shipment"} ${metadata.shipment_id} privately and they’d like you to quote it.  Click here to view the ${metadata?.is_project ? "project" : "shipment"} details.`;
}

function getPrivateShipmentCancelledText(
  metadata: PrivateShipmentCreatedNotificationMetadata,
): string {
  return `${metadata.company_name}'s ${metadata?.is_project ? "project" : "shipment"} ${metadata.shipment_id} was cancelled before booking. Click here to view details.`;
}

function getCarrierDirectBookingAssignedText(
  metadata: CarrierDirectBookingAssignedMetadata,
): string {
  const rate = formatRateWithQuotingUnit(
    metadata.price,
    metadata.currency,
    metadata.unit_of_measure,
  );
  if (metadata.is_project) {
    if (metadata.loads_assigned === null) {
      return `${metadata.company_name} booked you on project ${metadata.shipment_id} at ${rate} for each load. Click to view the project.`;
    }
    const loads = metadata.loads_assigned === 1 ? "load" : "loads";
    return `${metadata.company_name} booked you on ${metadata.loads_assigned} ${loads} in project ${metadata.shipment_id} at ${rate} for each load. Click to view the project.`;
  }
  return `${metadata.company_name} booked you on shipment ${metadata.shipment_id} at ${rate}. Click to view the shipment.`;
}

function getCarrierFailedToJoinNetworkText(): string {
  return "A shipper is inviting you to their private network.  Upgrade your account to accept the invite.";
}

function getCarrierShipmentMatchedText(
  metadata: CarrierShipmentMatchedMetadata,
) {
  return `One of your saved lanes has matched with ${metadata?.is_project ? "project" : "shipment"} ${metadata.shipment_id}. Click to view the ${metadata?.is_project ? "project" : "shipment"}  and start sending quotes.`;
}

function getProjectCompletedText(metadata: ShipperProjectCompletedMetadata) {
  return `Congrats! Project ${metadata.project_id} has been completed. Click to view the details.`;
}

function getCarrierMultiShipmentProjectLoadsAssignedText(
  metadata: CarrierMultiShipmentLoadsAssignedMetadata,
): string {
  return `Congrats! shipper ${metadata.shipper_company_name} has assigned you ${metadata.loads_assigned} shipments on project ${metadata.project_id} at $${metadata.price} ${metadata.currency} per load. Click to view the project.`;
}

function getCarrierShipmentLost(metadata: CarrierProjectLostMetadata) {
  return `Your quote on ${metadata?.is_project ? "project" : "shipment"} ${metadata.shipment_id} was not selected and you lost the ${metadata?.is_project ? "project" : "shipment"}. Click to view the details.`;
}

function getCarrierBookingSharedText(
  metadata: CarrierBookingSharedMetadata,
): string {
  return `${metadata.shared_by_first_name} ${metadata.shared_by_last_name} has shared booking ${metadata.shipment_id} with you. Click here to view the booking.`;
}

function getSupplierCustomerNetworkInviteText(
  metadata: SupplierCustomerNetworkInviteMetadata,
): string {
  return `${metadata.supplier_first_name} ${metadata.supplier_last_name} at ${metadata.supplier_company_name} has invited you to join their customer network. Click to respond.`;
}

function getCarrierCSFShipmentInviteText(metadata: any): string {
  return `You have been added to ${metadata.shipper_company_name}'s network and invited to quote shipment ${metadata.shipment_id}. Click to view details.`;
}

function getShipperTfcAmendedText(metadata: ShipperTfcAmendedMetadata): string {
  return `The total freight cost for shipment ${metadata.shipment_display_name} has been amended by ${metadata.carrier_company_name}. Click to review.`;
}

function getJoinCompanyRequestCreatedText(
  metadata: JoinCompanyRequestCreatedMetadata,
): string {
  return `${metadata.first_name} ${metadata.last_name} has requested to join ${metadata.company_name}. Click to review.`;
}

function getJoinCompanyResponseText(
  type: NotificationType,
  metadata: JoinCompanyResponseMetadata,
): string {
  if (
    type === CUSTOM_FORMATTED_NOTIFICATION_TYPES.JOIN_COMPANY_REQUEST_APPROVED
  ) {
    return `Your request to join ${metadata.company_name} was approved. Click to go to your dashboard.`;
  }
  return `Your request to join ${metadata.company_name} was declined.`;
}

function getShipperNewQuoteText(metadata: ShipperNewQuoteMetadata): string {
  const shipmentRef = metadata.shipment_display_name ?? metadata.shipment_id;

  if (metadata.carrier_company_name && metadata.price && metadata.currency) {
    return `${metadata.carrier_company_name} submitted a quote for $${metadata.price} ${metadata.currency} on shipment ${shipmentRef}! Click to view details.`;
  }

  return `There's a new quote on shipment ${shipmentRef}! Click to view details.`;
}

function getCustomFormatNotificationDisplayText(
  type: NotificationType,
  metadata: unknown,
): string {
  try {
    if (type === CUSTOM_FORMATTED_NOTIFICATION_TYPES.SHIPPER_NEW_QUOTE) {
      return getShipperNewQuoteText(metadata as ShipperNewQuoteMetadata);
    }
    if (type === CUSTOM_FORMATTED_NOTIFICATION_TYPES.CARRIER_BOOKING_SHARED) {
      return getCarrierBookingSharedText(
        metadata as CarrierBookingSharedMetadata,
      );
    }
    if (
      type === CUSTOM_FORMATTED_NOTIFICATION_TYPES.SHIPPER_PROJECT_COMPLETED
    ) {
      return getProjectCompletedText(
        metadata as ShipperProjectCompletedMetadata,
      );
    }
    if (type === CUSTOM_FORMATTED_NOTIFICATION_TYPES.CARRIER_SHIPMENT_LOST) {
      return getCarrierShipmentLost(metadata as CarrierProjectLostMetadata);
    }
    if (
      type ===
      CUSTOM_FORMATTED_NOTIFICATION_TYPES.CARRIER_MULTI_SHIPMENT_PROJECT_LOADS_ASSIGNED
    ) {
      return getCarrierMultiShipmentProjectLoadsAssignedText(
        metadata as CarrierMultiShipmentLoadsAssignedMetadata,
      );
    }
    if (type === CUSTOM_FORMATTED_NOTIFICATION_TYPES.CARRIER_SHIPMENT_MATCHED) {
      return getCarrierShipmentMatchedText(
        metadata as CarrierShipmentMatchedMetadata,
      );
    }
    if (
      type ===
      CUSTOM_FORMATTED_NOTIFICATION_TYPES.CARRIER_FOLLOW_SHIPPER_REQUESTED_INTERNAL
    ) {
      return getFollowShipperRequestInternalText(
        metadata as FollowShipperRequestInternalMetadata,
      );
    }
    if (
      type ===
      CUSTOM_FORMATTED_NOTIFICATION_TYPES.CARRIER_FOLOWED_SHIPPER_SHIPMENT_CREATED
    ) {
      return getFollowedCarrierNewShipmentText(
        metadata as FollowedCarrierNewShipmentMetadata,
      );
    }
    if (
      type ===
      CUSTOM_FORMATTED_NOTIFICATION_TYPES.CARRIER_FOLLOW_SHIPPER_RESPONSE_ACCEPTED
    ) {
      return getShipperFollowRequestAcceptedText(
        metadata as FollowShipperRequestAcceptedMetadata,
      );
    }
    if (type === CUSTOM_FORMATTED_NOTIFICATION_TYPES.COMPANY_REVIEW_CREATED) {
      return "A shipper has left you a review. Click to view.";
    }
    if (
      type ===
      CUSTOM_FORMATTED_NOTIFICATION_TYPES.SHIPMENT_BOOKED_CHAT_CHANNEL_CREATED
    ) {
      return getShipmentBookedChatChannelCreated(
        metadata as ShipmentBookedChatChannelMetadata,
      );
    }
    if (
      type ===
        CUSTOM_FORMATTED_NOTIFICATION_TYPES.CARRIER_PRIVATE_SHIPMENT_INVITE ||
      type ===
        CUSTOM_FORMATTED_NOTIFICATION_TYPES.CARRIER_PRIVATE_SHPMENT_ALL_NETWORK_INVITE
    ) {
      return getPrivateShipmentCreatedMetadata(
        metadata as PrivateShipmentCreatedNotificationMetadata,
      );
    }
    if (
      type ===
      CUSTOM_FORMATTED_NOTIFICATION_TYPES.CARRIER_FAILED_TO_JOIN_NETWORK_ON_INSUFFICIENT_SLOTS
    ) {
      return getCarrierFailedToJoinNetworkText();
    }
    if (
      type ===
      CUSTOM_FORMATTED_NOTIFICATION_TYPES.CARRIER_ESSENTIALS_REACHED_MAXIMUM_BOOKINGS
    ) {
      return "You have reached the end of your free trial bookings. Upgrade today to book more loads!";
    }
    if (
      type ===
      CUSTOM_FORMATTED_NOTIFICATION_TYPES.PAID_SUBSCRIPTION_CARRIER_REACHED_MAXIMUM_BOOKINGS
    ) {
      return "You have reached the maximum number of bookings in your subscription. Upgrade to book more loads!";
    }
    if (
      type ===
      CUSTOM_FORMATTED_NOTIFICATION_TYPES.SUPPLIER_CUSTOMER_NETWORK_INVITE_CREATED
    ) {
      return getSupplierCustomerNetworkInviteText(
        metadata as SupplierCustomerNetworkInviteMetadata,
      );
    }
    if (
      type ===
      CUSTOM_FORMATTED_NOTIFICATION_TYPES.CARRIER_CSF_SHIPMENT_INVITE_CREATED
    ) {
      return getCarrierCSFShipmentInviteText(metadata);
    }
    if (type === CUSTOM_FORMATTED_NOTIFICATION_TYPES.SHIPPER__TFC_AMENDED) {
      return getShipperTfcAmendedText(metadata as ShipperTfcAmendedMetadata);
    }
    if (
      type === CUSTOM_FORMATTED_NOTIFICATION_TYPES.JOIN_COMPANY_REQUEST_CREATED
    ) {
      return getJoinCompanyRequestCreatedText(
        metadata as JoinCompanyRequestCreatedMetadata,
      );
    }
    if (
      type ===
        CUSTOM_FORMATTED_NOTIFICATION_TYPES.JOIN_COMPANY_REQUEST_APPROVED ||
      type === CUSTOM_FORMATTED_NOTIFICATION_TYPES.JOIN_COMPANY_REQUEST_DENIED
    ) {
      return getJoinCompanyResponseText(
        type,
        metadata as JoinCompanyResponseMetadata,
      );
    }
    if (
      type ===
      CUSTOM_FORMATTED_NOTIFICATION_TYPES.CARRIER_PRIVATE_SHIPMENT_CANCELLED
    ) {
      return getPrivateShipmentCancelledText(
        metadata as PrivateShipmentCreatedNotificationMetadata,
      );
    }
    if (
      type ===
      CUSTOM_FORMATTED_NOTIFICATION_TYPES.CARRIER_DIRECT_BOOKING_ASSIGNED
    ) {
      return getCarrierDirectBookingAssignedText(
        metadata as CarrierDirectBookingAssignedMetadata,
      );
    }
    if (
      type ===
      CUSTOM_FORMATTED_NOTIFICATION_TYPES.SHIPPER_SHIPMENT_TEMPLATE_SHARED
    ) {
      return getShipperShipmentTemplateSharedText(
        metadata as ShipperShipmentTemplateSharedMetadata,
      );
    }
    return "-";
  } catch {
    return "-";
  }
}

export function getNotificationMessage(
  type: NotificationType,
  metadata: unknown,
): string {
  if (type in SUPPORTED_NOTIFICATIONS) {
    const meta = metadata as {
      shipment_display_name?: string;
      shipment_id?: number | string;
    };
    const formatter =
      SUPPORTED_NOTIFICATIONS[type as keyof typeof SUPPORTED_NOTIFICATIONS];
    return formatter(meta.shipment_display_name ?? meta.shipment_id ?? "");
  }
  if (type in CUSTOM_FORMATTED_NOTIFICATION_TYPES) {
    return getCustomFormatNotificationDisplayText(type, metadata);
  }
  return "-";
}

export function isNotificationUnread(notification: Notification): boolean {
  return notification.read_at == null;
}
